import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import {
  formatObjectsToIds,
  parseIdsToObjects,
  yankBy,
  isOtherType,
  serializeOptions,
} from 'utils'

import {
  ScrollField as Field,
  HiddenLegend,
  CheckboxWithText,
  CheckboxFieldset,
  CheckboxGroup,
} from 'components'
import { startCase, isEmpty } from 'lodash'
import danceImage from 'images/discipline-icons/dance.svg'
import literaryImage from 'images/discipline-icons/literary.svg'
import musicImage from 'images/discipline-icons/music.svg'
import theaterImage from 'images/discipline-icons/theater.svg'
import visualArtsImage from 'images/discipline-icons/visual-arts.svg'
import mediaArtsImage from 'images/discipline-icons/media-arts.svg'
import otherImage from 'images/discipline-icons/other.svg'
import * as Types from 'types'

const propTypes = {
  approachTypes: PropTypes.arrayOf(Types.communityEnumerable).isRequired,
  disciplines: PropTypes.arrayOf(Types.communityEnumerable).isRequired,
  outcomeTypes: PropTypes.arrayOf(Types.communityEnumerable).isRequired,
  programTypes: PropTypes.arrayOf(Types.communityEnumerable).isRequired,
  languages: PropTypes.arrayOf(Types.communityEnumerable).isRequired,
}

const DISCIPLINE_IMAGE_MAP = {
  dance: danceImage,
  literary_arts: literaryImage,
  music: musicImage,
  theatre: theaterImage,
  visual_arts: visualArtsImage,
  media_arts: mediaArtsImage,
  other: otherImage,
}

const defaultProps = {}

function SkillSection({
  approachTypes,
  disciplines,
  outcomeTypes,
  programTypes,
  languages,
}) {
  const uniqueId = (inputName) => `skills-${inputName}`

  const [otherApproachType, definedApproachTypes] = useMemo(
    () => yankBy(approachTypes, isOtherType),
    [approachTypes]
  )

  const [otherOutcomeType, definedOutcomeTypes] = useMemo(
    () => yankBy(outcomeTypes, isOtherType),
    [outcomeTypes]
  )

  const [otherLanguage, definedLanguages] = useMemo(
    () => yankBy(languages, isOtherType),
    [languages]
  )

  return (
    <section className="skills-section">
      {!isEmpty(approachTypes) && (
        <CheckboxFieldset>
          <legend>
            <strong>Approaches</strong>
          </legend>
          <div className="response-option-blocks full-width">
            <div className="response-option-block">
              <Field
                className="approaches-checkbox-group"
                name="approaches"
                id={uniqueId('approaches')}
                labelComponent={HiddenLegend}
                component={CheckboxGroup}
                format={formatObjectsToIds('approachId')}
                parse={parseIdsToObjects('approachId')}
                options={serializeOptions(definedApproachTypes)}
              />
              {otherApproachType && (
                <Field
                  name="otherApproach.otherText"
                  id={uniqueId('otherApproach.otherText')}
                  label={otherApproachType.displayName}
                  placeholder="Description..."
                  component={CheckboxWithText}
                  className="CheckboxGroup full-width-other"
                />
              )}
            </div>
          </div>
        </CheckboxFieldset>
      )}
      {!isEmpty(languages) && (
        <CheckboxFieldset>
          <legend>
            <strong>
              What languages do you speak and provide programs in?
            </strong>
          </legend>
          <div className="response-option-blocks full-width">
            <div className="response-option-block">
              <Field
                name="languages"
                id={uniqueId('languages')}
                labelComponent={HiddenLegend}
                component={CheckboxGroup}
                className={
                  definedLanguages.length > 10 ? 'language-columns' : undefined
                }
                format={formatObjectsToIds('languageId')}
                parse={parseIdsToObjects('languageId')}
                options={serializeOptions(definedLanguages)}
              />
              {otherLanguage && (
                <Field
                  name="otherLanguage.otherText"
                  id={uniqueId('otherLanguage.otherText')}
                  label={otherLanguage.displayName}
                  placeholder="Description..."
                  component={CheckboxWithText}
                  className="CheckboxGroup full-width-other"
                />
              )}
            </div>
          </div>
        </CheckboxFieldset>
      )}
      <fieldset>
        <legend>
          <strong>Discipline (Primary and secondary)</strong>
        </legend>
        <div className="response-option-blocks">
          {disciplines.map(({ name, subDisciplines }, i) => (
            <div key={i} className="response-option-block">
              <p>
                <img
                  src={DISCIPLINE_IMAGE_MAP[name]}
                  alt=""
                  className="discipline-icon"
                />
                <strong>{startCase(name)}</strong>
              </p>

              <Field
                name="subDisciplines"
                id={uniqueId('subDisciplines')}
                label={false}
                component={CheckboxGroup}
                format={formatObjectsToIds('subDisciplineId')}
                parse={parseIdsToObjects('subDisciplineId')}
                options={serializeOptions(subDisciplines)}
              />
            </div>
          ))}
        </div>
      </fieldset>
      {!isEmpty(outcomeTypes) && (
        <CheckboxFieldset>
          <legend>
            <strong>Teaching Qualities and Intended Outcomes</strong>
          </legend>
          <div className="response-option-blocks full-width">
            <div className="response-option-block">
              <Field
                name="outcomes"
                id={uniqueId('outcomes')}
                labelComponent={HiddenLegend}
                component={CheckboxGroup}
                format={formatObjectsToIds('outcomeTypeId')}
                parse={parseIdsToObjects('outcomeTypeId')}
                options={serializeOptions(definedOutcomeTypes)}
              />
              {otherOutcomeType && (
                <Field
                  name="otherOutcome.otherText"
                  id={uniqueId('otherOutcome.otherText')}
                  label={otherOutcomeType.displayName}
                  placeholder="Description..."
                  component={CheckboxWithText}
                  className="CheckboxGroup full-width-other"
                />
              )}
            </div>
          </div>
        </CheckboxFieldset>
      )}
      <fieldset>
        <legend>
          <strong>
            Please select the types of arts programs that students at your
            school have access to and whether those programs are offered in
            school, out of school, or both.
          </strong>
        </legend>
        <div className="response-option-blocks full-width">
          <div className="response-option-block">
            <Field
              name="programTypes"
              id={uniqueId('programTypes')}
              label={false}
              component={CheckboxGroup}
              format={formatObjectsToIds('programTypeId')}
              parse={parseIdsToObjects('programTypeId')}
              options={serializeOptions(programTypes)}
            />
          </div>
        </div>
      </fieldset>
    </section>
  )
}

SkillSection.propTypes = propTypes
SkillSection.defaultProps = defaultProps

export default SkillSection
